import { isNativeAppWebview } from 'core/utils/webapp';
import { v4 as uuidv4 } from 'uuid';

import packageJson from '../../../package.json';
import { buildEvent, utf8ByteLength } from './analyticsEvent';
import { redactRouteSecrets } from './analyticsRoute';

import type { AnalyticsEvent, AnalyticsFlatRecord, AnalyticsProducer, AnalyticsUser } from './analyticsEvent';

export const EVENTS_ENDPOINT = 'v3/events';
// Máximo de eventos por petición del contrato de POST /api/v3/events (CAM-16).
export const MAX_BATCH_SIZE = 50;
export const FLUSH_DELAY_MS = 5000;
// Fetch limita a 64 KiB la suma de los cuerpos keepalive en vuelo, y la cuota es de toda la página:
// posthog-js (1.369.2) también pide keepalive en sus POST de menos de 52428 bytes. Con la mitad, el
// flush al cerrar la pestaña no le quita el sitio al último envío de PostHog.
export const MAX_KEEPALIVE_BODY_BYTES = 32 * 1024;

export type AnalyticsHeaders = Record<string, string>;
export type AnalyticsIdentity = { language?: string | null; schoolGuid?: string | null };
export type AnalyticsConfig = { baseUrl: string; getHeaders: () => AnalyticsHeaders };
export type SessionAuth = { authToken?: string | null; loginContext?: string | null };

type FetchLike = (url: string, init: RequestInit) => Promise<unknown>;
type ListenerTarget = {
  addEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
  removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
};
type VisibilityTarget = ListenerTarget & { visibilityState?: DocumentVisibilityState };
type ReadyListener = (isReady: boolean) => void;

export type AnalyticsClientDeps = {
  documentTarget?: VisibilityTarget | null;
  fetch?: FetchLike;
  getContext?: () => AnalyticsFlatRecord;
  now?: () => Date;
  producer?: AnalyticsProducer;
  uuid?: () => string;
  windowTarget?: ListenerTarget | null;
};

export type AnalyticsClient = {
  configure: (config: AnalyticsConfig) => void;
  flush: () => Promise<void>;
  identify: (identity: AnalyticsIdentity | null) => void;
  isReady: () => boolean;
  subscribe: (listener: ReadyListener) => () => void;
  teardown: () => void;
  track: (eventType: string, payload?: unknown) => boolean;
};

type FlushOptions = { isLeavingPage: boolean };

const BATCH_BODY_OVERHEAD_BYTES = utf8ByteLength(JSON.stringify({ events: [] }));

// fetch y no axios (CAM-16): el interceptor de setupAxios manda a /logout ante un 401 o un 403 de
// sesión, y un fallo de v3/events no puede cerrar la sesión del usuario. Se resuelve al enviar, no al
// crear el cliente, para que un test pueda sustituir globalThis.fetch.
function defaultFetch(url: string, init: RequestInit): Promise<unknown> {
  return globalThis.fetch(url, init);
}

function defaultContext(): AnalyticsFlatRecord {
  // Sin query string ni hash, que pueden llevar el token de sesión (URLs de ficheros del storage), y
  // con el patrón en vez del token en las rutas que lo llevan en el path (`/auth/login/:token`…).
  return { is_app: isNativeAppWebview(), route: redactRouteSecrets(globalThis.location?.pathname ?? '') };
}

function ignoreDeliveryFailure(): void {
  // Sin reintentos ni registro (decisión de CAM-16): perder un lote de analítica no afecta al usuario.
}

function toUser(identity: AnalyticsIdentity): AnalyticsUser {
  const user: AnalyticsUser = {};
  if (identity.schoolGuid) user.school_guid = identity.schoolGuid;
  if (identity.language) user.language = identity.language;
  return user;
}

/** Las mismas cabeceras de sesión que pone setupAxios (`core/utils/axios.js`): el token va sin `Bearer`. */
export function sessionHeaders(auth: SessionAuth | null | undefined): AnalyticsHeaders {
  const headers: AnalyticsHeaders = {};
  if (auth?.authToken) headers.Authorization = auth.authToken;
  if (auth?.loginContext) headers.login_context = auth.loginContext;
  return headers;
}

function headersKey(headers: AnalyticsHeaders): string {
  return JSON.stringify(
    Object.keys(headers)
      .sort()
      .map((name) => [name, headers[name]])
  );
}

function joinUrl(baseUrl: string, path: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${base}/${path}`;
}

function splitBySize(events: AnalyticsEvent[], maxBytes: number): string[] {
  const bodies: string[] = [];
  let chunk: string[] = [];
  let chunkBytes = BATCH_BODY_OVERHEAD_BYTES;

  events.forEach((event) => {
    const serialized = JSON.stringify(event);
    const eventBytes = utf8ByteLength(serialized) + 1;
    if (chunk.length > 0 && chunkBytes + eventBytes >= maxBytes) {
      bodies.push(`{"events":[${chunk.join(',')}]}`);
      chunk = [];
      chunkBytes = BATCH_BODY_OVERHEAD_BYTES;
    }
    chunk.push(serialized);
    chunkBytes += eventBytes;
  });
  if (chunk.length > 0) bodies.push(`{"events":[${chunk.join(',')}]}`);
  return bodies;
}

export function createAnalyticsClient(deps: AnalyticsClientDeps = {}): AnalyticsClient {
  const {
    documentTarget = globalThis.document ?? null,
    fetch: fetchImpl = defaultFetch,
    getContext = defaultContext,
    now = (): Date => new Date(),
    producer = { app: 'frontoffice', platform: 'web', version: packageJson.version },
    uuid = uuidv4,
    windowTarget = globalThis.window ?? null,
  } = deps;

  const sessionId = uuid();
  const readyListeners = new Set<ReadyListener>();
  let config: AnalyticsConfig | null = null;
  let user: AnalyticsUser | null = null;
  let queue: AnalyticsEvent[] = [];
  let queueHeaders: AnalyticsHeaders = {};
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let wasReady = false;

  function isReady(): boolean {
    return config !== null && user !== null;
  }

  function notifyIfReadyChanged(): void {
    const ready = isReady();
    if (ready === wasReady) return;
    wasReady = ready;
    readyListeners.forEach((listener) => listener(ready));
  }

  function clearFlushTimer(): void {
    if (flushTimer === null) return;
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  function send(url: string, headers: AnalyticsHeaders, body: string, keepalive: boolean): Promise<void> {
    try {
      const request = fetchImpl(url, {
        body,
        headers: { ...headers, 'Content-Type': 'application/json' },
        keepalive,
        method: 'POST',
      });
      return Promise.resolve(request).then(ignoreDeliveryFailure, ignoreDeliveryFailure);
    } catch {
      // Un fetch que lanza en el acto (sin soporte, cuerpo inválido) no puede romper a quien emite el evento.
      return Promise.resolve();
    }
  }

  function flushQueue({ isLeavingPage }: FlushOptions): Promise<void> {
    clearFlushTimer();
    if (queue.length === 0 || config === null) return Promise.resolve();

    const sentAt = now().toISOString();
    const events = queue.map((event) => ({ ...event, sent_at: sentAt }));
    const headers = queueHeaders;
    const url = joinUrl(config.baseUrl, EVENTS_ENDPOINT);
    queue = [];
    queueHeaders = {};

    if (!isLeavingPage) return send(url, headers, JSON.stringify({ events }), false);

    // Los trozos que no caben en el cupo keepalive salen sin él: si la pestaña solo se ha ocultado,
    // llegan igual; si se está cerrando, el navegador rechazaría el keepalive de todos modos.
    let keepaliveBudget = MAX_KEEPALIVE_BODY_BYTES;
    const requests = splitBySize(events, MAX_KEEPALIVE_BODY_BYTES).map((body) => {
      const bodyBytes = utf8ByteLength(body);
      const keepalive = bodyBytes <= keepaliveBudget;
      if (keepalive) keepaliveBudget -= bodyBytes;
      return send(url, headers, body, keepalive);
    });
    return Promise.all(requests).then(ignoreDeliveryFailure);
  }

  function handlePageHide(): void {
    flushQueue({ isLeavingPage: true });
  }

  function handleVisibilityChange(): void {
    if (documentTarget?.visibilityState === 'hidden') flushQueue({ isLeavingPage: true });
  }

  // addEventListener ignora la misma función repetida: configure puede llamarse más de una vez.
  function attachListeners(): void {
    windowTarget?.addEventListener('pagehide', handlePageHide);
    documentTarget?.addEventListener('visibilitychange', handleVisibilityChange);
  }

  function detachListeners(): void {
    windowTarget?.removeEventListener('pagehide', handlePageHide);
    documentTarget?.removeEventListener('visibilitychange', handleVisibilityChange);
  }

  function configure(nextConfig: AnalyticsConfig): void {
    config = nextConfig;
    attachListeners();
    notifyIfReadyChanged();
  }

  function identify(identity: AnalyticsIdentity | null): void {
    if (identity === null) {
      // Con las cabeceras capturadas al encolar, porque el logout ya ha borrado el token del store, y
      // con keepalive, porque Logout redirige enseguida (50 ms en app) y el navegador abortaría el fetch.
      flushQueue({ isLeavingPage: true });
      user = null;
    } else {
      user = toUser(identity);
    }
    notifyIfReadyChanged();
  }

  function track(eventType: string, payload: unknown = {}): boolean {
    const currentConfig = config;
    const currentUser = user;
    if (currentConfig === null || currentUser === null) return false;

    const headers = currentConfig.getHeaders();
    // Entre el reducer Logout y el identify(null) de InitsContainer el store ya no tiene token:
    // api-v3 respondería 401 a ese lote.
    if (!headers.Authorization) return false;

    const event = buildEvent(eventType, payload, {
      context: getContext(),
      eventId: uuid(),
      occurredOn: now().toISOString(),
      producer,
      sessionId,
      user: currentUser,
    });
    if (event === null) return false;

    // Un lote viaja con un único token: si la sesión ha cambiado, lo pendiente sale con el anterior.
    if (queue.length > 0 && headersKey(headers) !== headersKey(queueHeaders)) flushQueue({ isLeavingPage: false });

    queue.push(event);
    queueHeaders = headers;
    if (queue.length >= MAX_BATCH_SIZE) flushQueue({ isLeavingPage: false });
    else if (flushTimer === null) flushTimer = setTimeout(() => flushQueue({ isLeavingPage: false }), FLUSH_DELAY_MS);
    return true;
  }

  function subscribe(listener: ReadyListener): () => void {
    readyListeners.add(listener);
    return () => {
      readyListeners.delete(listener);
    };
  }

  function teardown(): void {
    detachListeners();
    clearFlushTimer();
    queue = [];
    queueHeaders = {};
    config = null;
    user = null;
    notifyIfReadyChanged();
    readyListeners.clear();
  }

  return {
    configure,
    flush: () => flushQueue({ isLeavingPage: false }),
    identify,
    isReady,
    subscribe,
    teardown,
    track,
  };
}

export const analytics = createAnalyticsClient();
